<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <div id="page-header" class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-green-800">Organization Management</h2>
          <p class="mt-2 text-gray-600 text-sm">Manage all organizations in the system</p>
        </div>
        <button id="create-org-btn" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
          rounded-md flex items-center
          space-x-2 font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
          focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]"
          @click="showCreateModal = true" v-if="authStore.hasPermissions('manage_organizations', 'local')">
          <i class="fa-solid fa-building mr-1"></i>
          <span>Create Organization</span>
        </button>
      </div>
    </div>
    <!-- Create Organization Modal -->
    <Modal :show="showCreateModal" title="Create Organization" size="max-w-4xl" @close="showCreateModal = false">
      <div v-if="loading" class="flex justify-center items-center h-32">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <form class="space-y-6" @submit.prevent="handleSubmit" v-if="!loading">
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Organization Name</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-building text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.name" type="text" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="Organization Name" />
            <p class="text-gray-500 text-sm">The legal name of the organization</p>
          </div>
        </div>
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Contact Name</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-user text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.contactName" type="text" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="John Doe" />
            <p class="text-gray-500 text-sm">Primary contact person for the organization</p>
          </div>
        </div>
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Contact Email</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-envelope text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.contactEmail" type="email" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="contact@example.com" />
            <p class="text-gray-500 text-sm">Email address for organization communications</p>
          </div>
        </div>
        <div class="flex justify-end space-x-2 mt-5">
          <button type="button" @click="closeModal" class="px-4 py-1 bg-gray-300 rounded-sm hover:bg-gray-400">
            Cancel
          </button>
          <button type="submit" :disabled="organizationStore.loading" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
            rounded-md flex items-center space-x-2 font-medium text-white bg-gradient-to-r from-green-700
            to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
            focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]
            disabled:opacity-50">
            <i class="fa-solid fa-save mr-2"></i>
            {{ organizationStore.loading ? 'Processing...' : 'Create Organization' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Edit Organization Modal -->
    <Modal :show="showEditModal" title="Edit Organization" size="max-w-4xl" @close="closeModal">
      <div v-if="loading" class="flex justify-center items-center h-32">
        <lottie-animation path="assets/images/loading.json" :width="150" :height="150" />
      </div>
      <form class="space-y-6" @submit.prevent="handleSubmit" v-if="!loading">
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Organization Name</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-building text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.name" type="text" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="Organization Name" />
            <p class="text-gray-500 text-sm">The legal name of the organization</p>
          </div>
        </div>
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Contact Name</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-user text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.contactName" type="text" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="John Doe" />
            <p class="text-gray-500 text-sm">Primary contact person for the organization</p>
          </div>
        </div>
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Contact Email</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-envelope text-green-600 leading-none"></i>
            </div>
            <input v-model="formData.contactEmail" type="email" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent" placeholder="contact@example.com" />
            <p class="text-gray-500 text-sm">Email address for organization communications</p>
          </div>
        </div>
        <div class="space-y-2 mb-6">
          <label class="block text-sm font-medium text-gray-600">Status</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 mb-5 flex items-center justify-center pointer-events-none">
              <i class="fa-solid fa-toggle-on text-green-600 leading-none"></i>
            </div>
            <select v-model="formData.status" required class="block w-full pl-10 pr-3 py-3 bg-white/10 border border-gray-500/20 rounded-sm
              border-gray-300 text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2
              focus:ring-green-500 focus:border-transparent">
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="DISABLED">Disabled</option>
            </select>
            <p class="text-gray-500 text-sm">Current status of the organization</p>
          </div>
        </div>
        <div class="flex justify-end space-x-2 mt-5">
          <button type="button" @click="closeModal" class="px-4 py-1 bg-gray-300 rounded-sm hover:bg-gray-400">
            Cancel
          </button>
          <button type="submit" :disabled="organizationStore.loading" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
            rounded-md flex items-center space-x-2 font-medium text-white bg-gradient-to-r from-green-700
            to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
            focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]
            disabled:opacity-50">
            <i class="fa-solid fa-save mr-2"></i>
            {{ organizationStore.loading ? 'Processing...' : 'Update Organization' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Organizations Table -->
    <div id="organizations-table" class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div v-if="organizationStore.loading" class="flex justify-center items-center h-48">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <!--      <div v-else-if="organizationStore.organizationResults.length === 0" class="p-8 text-center text-gray-500">-->
      <!--        No organizations found-->
      <!--      </div>-->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Person
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Email
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="org in organizationStore.organizationResults" :key="org.id">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ org.name }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ org.contactName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ org.contactEmail }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="{
                  'px-2 inline-flex text-xs leading-5 font-semibold rounded-full': true,
                  'bg-green-100 text-green-800': org.status === 'Active',
                  'bg-yellow-100 text-yellow-800': org.status === 'Inactive',
                  'bg-red-100 text-red-800': org.status === 'Disabled'
                }">
                  {{ org.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(org.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex space-x-3">
                  <button @click="viewOrganization(org.id)" class="text-green-600 hover:text-green-900"
                    title="View Organization">
                    <i class="fa-solid fa-eye"></i>
                  </button>
                  <template v-if="canManageOrganizations">
                    <button @click="editOrganization(org)" class="text-indigo-600 hover:text-indigo-900"
                      title="Edit Organization">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button v-if="org.status !== 'disabled'" @click="disableOrganization(org.id)"
                      class="text-red-600 hover:text-red-900" title="Disable Organization">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="organizationStore.pagination.totalPages && organizationStore.pagination.totalPages > 1"
        class="px-6 py-4 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Showing <span class="font-medium">{{ organizationStore.pagination.currentPage }}</span> of <span
              class="font-medium">{{ organizationStore.pagination.totalPages }}</span> results
          </div>
          <div class="flex items-center space-x-2" v-if="organizationStore.pagination.nextPage > 0">
            <button class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-500 hover:bg-gray-50"
              disabled>Previous</button>
            <button class="px-3 py-1 bg-blue-600 text-white rounded text-sm">{{
              organizationStore.pagination.nextPage }}</button>
            <button class="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useOrganizationStore } from '@/store/organizations';
import { useNotificationStore } from '@/store/notifications';
import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue';
import { useAuthStore } from '@/store/auth';
import Header from '../components/header.vue';
import SideBar from '../components/sidebar.vue';
import Modal from '../components/modal.vue';

interface Organization {
  id: string;
  name: string;
  status: string;
  contactName: string;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
}

const organizationStore = useOrganizationStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();
const router = useRouter();

const showCreateModal = ref<boolean>(false);
const showEditModal = ref<boolean>(false);
const currentPage = ref<number>(1);
const editingOrg = ref<Organization | null>(null);
const loading = ref<boolean>(false);

const formData = ref({
  name: '',
  contactName: '',
  contactEmail: '',
  status: 'active',
});

const canManageOrganizations = computed(() => {
  const purpose = localStorage.getItem('purpose');
  if (purpose !== 'local') return false;

  const permissions = localStorage.getItem('permissions');
  if (!permissions) return false;
  const permissionList = permissions.split(',').map((p) => p.trim().toLowerCase());
  return permissionList.includes('manage_organizations');
});

onMounted(() => {
  organizationStore.fetchOrganizations(currentPage.value);
});

watch(currentPage, (newPage) => {
  organizationStore.fetchOrganizations(newPage);
});

function formatDate(input: string) {
  const date = new Date(input);
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
  return formatted;
}

function viewOrganization(id: string) {
  router.push({ name: 'organization-detail', params: { id } });
}

function editOrganization(org: Organization) {
  editingOrg.value = org;
  formData.value = {
    name: org.name,
    contactName: org.contactName,
    contactEmail: org.contactEmail,
    status: org.status,
  };
  showEditModal.value = true;
}

async function disableOrganization(id: string) {
  if (!confirm('Are you sure you want to disable this organization?')) return;

  loading.value = true;
  try {
    await organizationStore.disableOrganization(id);

    if (organizationStore.error) {
      notificationStore.showError('Disable Failed', organizationStore.error);
    } else {
      notificationStore.showSuccess('Organization Disabled', 'The organization has been disabled successfully.');
      organizationStore.fetchOrganizations(currentPage.value);
    }
  } catch (err) {
    notificationStore.showError('Disable Error', 'An unexpected error occurred while disabling the organization.');
  } finally {
    loading.value = false;
  }
}

async function handleSubmit() {
  if (!formData.value.name || !formData.value.contactName || !formData.value.contactEmail) {
    notificationStore.showError('Validation Error', 'Please fill in all required fields.');
    return;
  }

  loading.value = true;
  try {
    let success = false;

    if (showCreateModal.value) {
      success = await organizationStore.createOrganization(
        formData.value.name,
        formData.value.contactName,
        formData.value.contactEmail,
      );

      if (organizationStore.error) {
        notificationStore.showError('Create Failed', organizationStore.error);
      } else {
        notificationStore.showSuccess('Organization Created', 'New organization has been created successfully.');
        closeModal();
        organizationStore.fetchOrganizations(currentPage.value);
      }
    } else if (showEditModal.value && editingOrg.value) {
      success = await organizationStore.updateOrganization(
        editingOrg.value.id,
        formData.value.name,
        formData.value.contactName,
        formData.value.contactEmail,
        formData.value.status,
      );

      if (organizationStore.error) {
        notificationStore.showError('Update Failed', organizationStore.error);
      } else {
        notificationStore.showSuccess('Organization Updated', 'Organization has been updated successfully.');
        closeModal();
        organizationStore.fetchOrganizations(currentPage.value);
      }
    }
  } catch (err) {
    notificationStore.showError('Error', 'An unexpected error occurred.');
  } finally {
    loading.value = false;
  }
}

function closeModal() {
  showCreateModal.value = false;
  showEditModal.value = false;
  editingOrg.value = null;
  formData.value = {
    name: '',
    contactName: '',
    contactEmail: '',
    status: 'active',
  };
  organizationStore.clearMessages();
}
</script>
